/**
 * useJunctionLabelResolver (Item 5) — resolves Subcategory / Specialty
 * slugs to their display names for the suggestion review surfaces.
 *
 * Suggested-edit / change-log values for the junction fields store SLUGS
 * (the stable identity). The detail panes that review them (steward review,
 * "My suggestions") want pretty labels, so they fetch the lookup vocab
 * once (cached, slug-keyed) and pass the returned resolver to
 * humanizeFieldValue. Compact/timeline surfaces skip this and fall back to
 * the slug — humanizeFieldValue never renders raw JSON either way.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { lookupsApi } from './lookups';

/**
 * Returns a `slug → display name` resolver. When `enabled` is false (the
 * suggestion isn't a junction field), no fetch runs and the resolver is an
 * identity (returns the slug). Unmapped slugs (e.g. deprecated) also fall
 * back to the slug.
 */
export function useJunctionLabelResolver(
  tenantSlug: string,
  enabled: boolean,
): (slug: string) => string {
  const subcategoryQuery = useQuery({
    queryKey: ['lookups', 'subcategories', 'all', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listSubcategories(tenantSlug, { limit: 500 }, signal),
    enabled,
    staleTime: 5 * 60_000,
  });
  const specialtyQuery = useQuery({
    queryKey: ['lookups', 'specialties', 'all', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listSpecialties(tenantSlug, { limit: 500 }, signal),
    enabled,
    staleTime: 5 * 60_000,
  });

  return useMemo(() => {
    const map = new Map<string, string>();
    for (const r of subcategoryQuery.data?.rows ?? []) map.set(r.slug, r.display_name);
    for (const r of specialtyQuery.data?.rows ?? []) map.set(r.slug, r.display_name);
    return (slug: string): string => map.get(slug) ?? slug;
  }, [subcategoryQuery.data, specialtyQuery.data]);
}
